import { Typography } from '@euler/app/components/typography';
import { Img } from '@euler/components/image/AdvancedImage';
import {
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
} from '@euler/functions/getDefectiveLevel';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  DefectiveLevel,
  FacadeSiteConfig,
  FacadeSiteConfigurations,
  InspectionSiteInfo,
} from '@euler/model';
import { array2map } from '@euler/utils/array';
import { memo, useCallback, useMemo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

const kOuterRadius = 8;
const kInnerRadius = 2.5;
const kMargin = 30;

export const useFacadeIllustrationViewHeightByWidth = (width: number) => {
  const { rpx } = useResponsiveContext();
  return width - rpx(2 * kMargin);
};

export const FacadeIllustrationView = memo(
  ({
    size,
    style,
    sites,
    siteCodeSetWithDefect,
    onSitePress,
  }: {
    sites: InspectionSiteInfo[];
    siteCodeSetWithDefect: Set<string>;
    style?: StyleProp<ViewStyle>;
    size: number;
    onSitePress?: (code: string) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const scale = (size - 2 * rpx(kMargin)) / 1100;
    const siteCodeMap = useMemo(() => array2map(sites, x => x.code), [sites]);
    const configurations = useMemo(
      () => FacadeSiteConfigurations.filter(x => siteCodeMap.has(x.code)),
      [siteCodeMap],
    );
    const defectiveBgColor = getDefectiveLevelBgColor(
      theme,
      DefectiveLevel.Urgent,
    );
    const defectiveColor = getDefectiveLevelColor(theme, DefectiveLevel.Urgent);
    return (
      <View style={style}>
        <VerticalTextView
          text="左侧车身"
          css={`
            left: 0;
          `}
        />
        <VerticalTextView
          text="右侧车身"
          css={`
            right: 0;
          `}
        />
        <View
          css={`
            padding-left: ${rpx(kMargin)}px;
            padding-right: ${rpx(kMargin)}px;
            align-items: center;
          `}
        >
          <View
            css={`
              width: ${size - 2 * rpx(kMargin)}px;
              height: ${size - 2 * rpx(kMargin)}px;
            `}
          >
            <Img
              source={require('@euler/assets/img/car-facade-views.png')}
              css={`
                width: ${size - 2 * rpx(kMargin)}px;
                height: ${size - 2 * rpx(kMargin)}px;
              `}
            />
            {configurations.map(siteConfig => (
              <TouchableSite
                key={siteConfig.code}
                siteConfig={siteConfig}
                scale={scale}
                onPress={onSitePress}
                defectiveBgColor={defectiveBgColor}
                defectiveColor={defectiveColor}
                hasDefect={siteCodeSetWithDefect.has(siteConfig.code)}
              />
            ))}
          </View>
        </View>
      </View>
    );
  },
);

const TouchableSite = memo(
  ({
    siteConfig,
    scale,
    defectiveBgColor,
    defectiveColor,
    hasDefect,
    onPress,
  }: {
    siteConfig: FacadeSiteConfig;
    scale: number;
    defectiveBgColor: string;
    defectiveColor: string;
    hasDefect: boolean;
    onPress?: (code: string) => void;
  }) => {
    const { code } = siteConfig;
    const { rpx } = useResponsiveContext();

    const [x1, y1, w, h] = siteConfig.coords.map(x => x * scale);

    const [dx, dy] = [
      siteConfig.offset?.[0] ?? 0,
      siteConfig.offset?.[1] ?? 0,
    ].map(x => x * scale);

    const [x, y] = [x1 + w / 2 + dx, y1 + h / 2 + dy];

    const onSitePress = useCallback(() => {
      onPress?.(code);
    }, [code, onPress]);

    const bgColor = hasDefect ? defectiveBgColor : 'rgba(0, 138, 255, 0.15)';
    const color = hasDefect ? defectiveColor : 'rgba(0, 138, 255, 1)';
    return (
      <>
        <TouchableOpacity
          hitSlop={PredefinedHitSlops.large}
          css={`
            position: absolute;
            width: ${rpx(kOuterRadius) * 2}px;
            height: ${rpx(kOuterRadius) * 2}px;
            border-radius: ${rpx(kOuterRadius)}px;
            background-color: ${bgColor};
            left: ${x - rpx(kOuterRadius)}px;
            top: ${y - rpx(kOuterRadius)}px;
            align-items: center;
            justify-content: center;
          `}
          onPress={onSitePress}
        >
          <View
            css={`
              width: ${rpx(kInnerRadius) * 2}px;
              height: ${rpx(kInnerRadius) * 2}px;
              border-radius: ${rpx(kInnerRadius)}px;
              background-color: ${color};
            `}
          />
        </TouchableOpacity>
      </>
    );
  },
);

const VerticalTextView = memo(
  ({ text, style }: { text: string; style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 0;
          bottom: 0;
          width: ${rpx(kMargin)}px;
        `}
        style={style}
      >
        <Typography.Label
          thin
          size={rpx(16)}
          color={'#666'}
          css={`
            text-align: center;
          `}
        >
          {text.split('').join('\n')}
        </Typography.Label>
      </View>
    );
  },
);
